import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

export interface StageFact {
  /** What it is: "Synced", "Seats". Left off for a line that speaks for itself. */
  label?: string;
  /** What it says: "2 min ago", "8 of 12". */
  value: string;
}

export interface Stage {
  id: string;
  /** The step: "Incoming leads". */
  title: string;
  /** What it is set to, in a word or two: "HubSpot", "Regal". */
  setting: string;
  /** Up to three lines under it. More than three and the card stops scanning. */
  facts?: StageFact[];
  /** A dot beside the setting: connected, needs attention, off. */
  state?: 'on' | 'warn' | 'off';
  icon?: React.ReactNode;
}

export interface StageFlowProps {
  stages: Stage[];
  /** The one being configured now, drawn as selected. */
  activeId?: string | null;
  /** Opens a stage. Without it the cards are read-only. */
  onOpen?: (id: string) => void;
  /**
   * Names and connections only — no facts inside the cards. For a header
   * whose job is to show how the pieces are joined rather than to report.
   */
  compact?: boolean;
}

/**
 * A pipeline, as cards with arrows between them.
 *
 * For a thing that is configured in steps and then runs — leads in, priority,
 * voice, dialer — where the point of the header is that an onlooker can see
 * the whole path at once and what each step is set to. Each card carries its
 * own live fact, so the row doubles as a status line: the flow is not a
 * diagram of the configuration, it *is* the configuration.
 *
 * Wraps to the next line rather than scrolling sideways, so nothing is hidden
 * off the edge on a laptop or a projector.
 */
export function StageFlow({ stages, activeId, onOpen, compact = false }: StageFlowProps) {
  return (
    <Stack direction="row" alignItems="stretch" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
      {stages.map((stage, index) => (
        <React.Fragment key={stage.id}>
          <ButtonBase
            onClick={onOpen ? () => onOpen(stage.id) : undefined}
            disableRipple={!onOpen}
            sx={(t) => ({
              flex: '1 1 180px',
              minWidth: 170,
              maxWidth: 320,
              textAlign: 'left',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              flexDirection: 'column',
              p: compact ? 1.25 : 1.5,
              borderRadius: `${t.ds.radius.md}px`,
              border: `1px solid ${activeId === stage.id ? t.tokens.brand.primary : t.tokens.border.subtle}`,
              bgcolor: activeId === stage.id ? t.tokens.brand.primarySoft : t.tokens.surface.raised,
              cursor: onOpen ? 'pointer' : 'default',
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '&:hover': onOpen ? { borderColor: t.tokens.brand.primary } : undefined,
            })}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              {stage.icon}
              <Typography
                variant="overline"
                sx={(t) => ({ color: t.tokens.text.tertiary, lineHeight: 1.4 })}
              >
                {stage.title}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.75}>
              {stage.state ? (
                <Box
                  sx={(t) => ({
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor:
                      stage.state === 'on' ? t.tokens.semantic.success
                        : stage.state === 'warn' ? t.tokens.semantic.warning
                          : t.tokens.text.disabled,
                  })}
                />
              ) : null}
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })} noWrap>
                {stage.setting}
              </Typography>
            </Stack>

            {(compact ? [] : (stage.facts || []).slice(0, 3)).map((fact, i) => (
              <Stack key={i} direction="row" spacing={0.75} sx={{ mt: 0.25, minWidth: 0 }}>
                {fact.label ? (
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })} noWrap>
                    {fact.label}
                  </Typography>
                ) : null}
                <Typography
                  variant="caption"
                  sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}
                  noWrap
                >
                  {fact.value}
                </Typography>
              </Stack>
            ))}
          </ButtonBase>

          {index < stages.length - 1 ? (
            <Stack justifyContent="center" sx={{ flexShrink: 0 }}>
              <ChevronRightRounded
                sx={(t) => ({ fontSize: 18, color: t.tokens.text.disabled })}
              />
            </Stack>
          ) : null}
        </React.Fragment>
      ))}
    </Stack>
  );
}

export default StageFlow;
